import { G } from '@mobily/ts-belt';
import { DataProvider } from 'react-admin';
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';

import { CampaignAllocationChoice } from '@Common/bo/flatten_campaign_allocations_per_choice';
import { CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE } from '@Common/constants/b2b/campaigns';
import { FULLY_MANAGED_PLAN_SUPPORT_LABEL } from '@Common/constants/employee_savings';
import {
  B2bSavingPlanDeductibilityName,
  B2bSavingPlanType,
  B2bSavingPlanTypeValues,
} from '@Common/types/b2b/plans';
import { B2bCampaignType } from '@Common/types/bo/b2b/campaigns';
import { B2bCompanyPlanTypeParameters } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponse } from '@Common/types/bo/instruments';
import { MAX_RANGE_ITEMS } from '@Common/types/bo/utils/build_zod_bo_list_query';

import CampaignAllocationsTemplate from '@sh4res/bo-common/components/B2bCampaigns/campaign_allocations_export_template.xlsm';
import CampaignAllocationsMlkTemplate from '@sh4res/bo-common/components/B2bCampaigns/campaign_allocations_export_template_mlk.xlsm';
import CampaignVoluntaryDepositAllocationsTemplate from '@sh4res/bo-common/components/B2bCampaigns/campaign_voluntary_deposit_allocations_export_template.xlsm';
import CampaignVoluntaryDepositAllocationsMlkTemplate from '@sh4res/bo-common/components/B2bCampaigns/campaign_voluntary_deposit_allocations_export_template_mlk.xlsm';
import { getBrandedFile } from '@sh4res/bo-common/theme';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { downloadBuffer } from '@sh4res/bo-common/utils/downloadBuffer';
import { isBoExternal } from '@sh4res/bo-common/utils/env';
import { writeRowsToSheet } from '@sh4res/bo-common/utils/xlsx';

function processChoiceData(
  choice: CampaignAllocationChoice,
  instruments: GetInstrumentsResponse,
): {
  planType: string;
  instrumentName: string;
} {
  let planType = '';
  let instrumentName = '';

  if (choice.allocation_type === 'payout') {
    planType = 'Versement compte bancaire';
  } else if (
    choice.plan_type !== null &&
    (B2bSavingPlanTypeValues as readonly string[]).includes(choice.plan_type)
  ) {
    planType = B2bCompanyPlanTypeParameters[choice.plan_type as B2bSavingPlanType].name;
    if (choice.plan_label) {
      planType = `${planType} (${choice.plan_label})`;
    }
  }

  if (G.isNotNullable(choice.deductibility)) {
    planType = `${planType} ${B2bSavingPlanDeductibilityName[choice.deductibility]}`;
  }

  if (choice.allocation_type === 'managed_plan') {
    instrumentName = FULLY_MANAGED_PLAN_SUPPORT_LABEL;
  } else if (choice.allocation_type === 'plan' && choice.instrument_id !== null) {
    const instrument = instruments.find((inst) => inst.id === choice.instrument_id);
    if (instrument?.type === 'ccb') {
      planType = 'CCB';
    }
    instrumentName = instrument
      ? `${instrument.isin} - ${instrument.short_name}`
      : `Instrument ${choice.instrument_id}`;
  }

  return { planType, instrumentName };
}

function convertAmountToEuros(amount: number | null | undefined): number {
  return (amount ?? 0) / 100;
}

export type BulkExportAllocationChoiceRow = CampaignAllocationChoice;

/**
 * Exports the draft allocations of the campaign form to an Excel file following Allocation 2.0
 * logic: one line per choice instead of one line per employee. A saved campaign's allocations
 * are exported by the backend (`campaign_allocations`), in the same file.
 */
export const bulkExportAllocations = async ({
  dataProvider,
  campaignType,
  initialData,
  documentName,
}: {
  dataProvider: DataProvider;
  campaignType: B2bCampaignType;
  initialData?: BulkExportAllocationChoiceRow[];
  documentName: string;
}): Promise<void> =>
  (async () => {
    try {
      const isVoluntaryDepositCampaign = campaignType === 'voluntary_deposit';
      const isCETCampaign = campaignType === 'unused_vacation_days';

      if (!initialData || initialData.length === 0) {
        throw new Error('No data available for export');
      }
      const choicesToProcess = initialData;

      const { data: instruments } = await dataProvider.getList<GetInstrumentsResponse[number]>(
        'instruments',
        {
          pagination: { page: 1, perPage: MAX_RANGE_ITEMS },
          sort: { field: isBoExternal() ? 'id' : 'created_at', order: 'ASC' },
          filter: {
            id: choicesToProcess
              .filter((choice) => G.isNotNullable(choice.instrument_id))
              .map((choice) => choice.instrument_id),
          },
        },
      );

      const operationRows: (string | number)[][] = [];

      choicesToProcess.forEach((choice) => {
        const { planType, instrumentName } = processChoiceData(choice, instruments);

        if (isVoluntaryDepositCampaign || isCETCampaign) {
          operationRows.push([
            choice.identifier || '',
            choice.firstname || '',
            choice.lastname || '',
            choice.registration_number || '',
            planType,
            instrumentName,
            convertAmountToEuros(choice.net_amount),
            convertAmountToEuros(choice.matching_contribution_gross_amount),
            convertAmountToEuros(choice.matching_contribution_net_amount),
          ]);
        } else {
          operationRows.push([
            choice.identifier || '',
            choice.firstname || '',
            choice.lastname || '',
            choice.registration_number || '',
            planType,
            instrumentName,
            convertAmountToEuros(choice.gross_amount),
            convertAmountToEuros(choice.net_amount),
            convertAmountToEuros(choice.csg_amount),
            convertAmountToEuros(choice.crds_amount),
            convertAmountToEuros(choice.late_interests_amount),
            convertAmountToEuros(choice.matching_contribution_gross_amount),
            convertAmountToEuros(choice.matching_contribution_net_amount),
          ]);
        }
      });

      const response = await fetch(
        isVoluntaryDepositCampaign || isCETCampaign
          ? getBrandedFile(
              CampaignVoluntaryDepositAllocationsTemplate,
              CampaignVoluntaryDepositAllocationsMlkTemplate,
            )
          : getBrandedFile(CampaignAllocationsTemplate, CampaignAllocationsMlkTemplate),
      );
      const fileBuffer = await response.arrayBuffer();
      const workbook = await XlsxPopulate.fromDataAsync(fileBuffer);
      const sheet = workbook.sheet('export');

      writeRowsToSheet(sheet, operationRows, CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE);

      const buffer = await workbook.outputAsync();

      downloadBuffer(buffer, documentName);
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  })();
